import { superscriptSymbols } from "hyam-core/utils/superscriptSymbols";
import _ from "lodash";
import { MenuDocument } from "prismicio-types";
import { withConfigure } from "hyam-core/utils/withConfigure";
import { styles as moduleStyles, HyamPrismicDocumentMenuStyles } from "./styles";
import { LinkField } from "@prismicio/client";
import CloseIcon from "hyam-core/svg/hamburger-close-24px.svg";
import Arrow from "svg/arrow-right-24px.svg";

import { SubmenuDocument } from "prismicio-types";
import { ReactElement, useEffect, useLayoutEffect, useRef, useState } from "react";
import { classNamesWithMerge as cn } from "hyam-core/utils/tailwindMerge";
import {
  HyamPrismicButton,
  HyamPrismicLangSwitchRow,
} from "hyam-prismic/components/elements";
import { dispatchEvent } from "hyam-core/utils/eventTarget";

/**
 * Grace period before a column closes. It gives the pointer time to travel to
 * the column it just opened, crossing sibling items on the way. Entering that
 * column cancels the pending close (see onColumnEnter).
 */
const CLOSE_DELAY = 300;

export type HyamMenuItem = {
  submenu: SubmenuDocument;
  link?: LinkField;
  title?: string;
};

export type HyamPrismicDocumentMenuProps = {
  document: MenuDocument;
  styles?: Partial<HyamPrismicDocumentMenuStyles>;
  stylesReplace?: Partial<HyamPrismicDocumentMenuStyles>;
};

type ColumnProps = HyamPrismicDocumentMenuProps & {
  items: HyamMenuItem[];
  onHover?: (items: HyamMenuItem[], index: number, el: HTMLElement) => void;
  onColumnEnter?: () => void;
  /** Center of the parent item's row, within the menu. Null: center on own content. */
  anchorCenter?: number | null;
  col: number;
  bottom?: ReactElement;
  doClose: () => void;
};

const Column = (props: ColumnProps) => {
  const styles = props.styles as HyamPrismicDocumentMenuStyles;
  const {
    items,
    onHover = () => {},
    onColumnEnter = () => {},
    anchorCenter = null,
    col,
    bottom = null,
    doClose = () => {},
  } = props;

  const isActive = !_.isEmpty(items);

  const colRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [offsetTop, setOffsetTop] = useState(0);

  /**
   * Fix for wrapping text to have width that matches text content
   *
   * https://stackoverflow.com/questions/33244477/wrapping-text-forces-containing-element-to-max-width/33246364#33246364
   *
   * Runs as a layout effect, and before the anchoring one: it can change a label
   * from two lines to one, and the anchor below has to measure the settled height.
   */
  useLayoutEffect(() => {
    const range = document.createRange();
    const blocks = document.querySelectorAll<HTMLElement>(".text-wrap-width-fix");

    Array.from(blocks).forEach((el) => {
      const text = el.childNodes[0];
      if (!text) return;

      // Drop the width written on a previous pass: measuring inside it would size
      // the new label to its wrapped width and lock in the wrap (AI-47).
      el.style.width = "";

      range.setStartBefore(text);
      range.setEndAfter(text);

      const clientRect = range.getBoundingClientRect();
      el.style.width = `${clientRect.width}px`;
    });
  });

  /**
   * Center the column on the row of the parent item that opened it, clamped so a
   * parent near an edge cannot push its children out of the viewport. Without an
   * anchor the column centers on its own content, so every item shifts whenever
   * the child count changes (AI-47).
   */
  useLayoutEffect(() => {
    if (anchorCenter === null || !colRef.current || !contentRef.current) return;

    const colHeight = colRef.current.getBoundingClientRect().height;
    const contentHeight = contentRef.current.getBoundingClientRect().height;
    const top = anchorCenter - contentHeight / 2;

    setOffsetTop(Math.max(0, Math.min(top, colHeight - contentHeight)));
  }, [anchorCenter, items]);

  const styles_col = cn(styles.col, {
    [styles.col_active]: isActive,
    [styles.col_inactive]: !isActive,
    [styles.col_0]: col === 0,
    [styles.col_1]: col === 1,
    [styles.col_2]: col === 2,
    [styles.col_centered]: anchorCenter === null,
    [styles.col_anchored]: anchorCenter !== null,
  });

  return (
    <div
      ref={colRef}
      className={styles_col}
      onClick={isActive ? undefined : doClose}
      onMouseEnter={onColumnEnter}
    >
      <div
        ref={contentRef}
        className={styles.col_content}
        style={anchorCenter === null ? undefined : { marginTop: offsetTop }}
      >
        {_.map(items, (item, index) => {
          const subItems = item?.submenu?.data?.items as HyamMenuItem[];

          const hasSubmenu = !!item?.submenu?.data;

          return (
            <div
              className={styles.col_item}
              key={index}
              onMouseEnter={(event) => onHover(subItems, index, event.currentTarget)}
            >
              <HyamPrismicButton
                field={item?.link}
                rightIcon={hasSubmenu ? <Arrow /> : null}
                styles={{ container_contained: styles.col_item_button }}
              >
                <span className="text-left text-wrap-width-fix">{superscriptSymbols(item?.title)}</span>
              </HyamPrismicButton>
            </div>
          );
        })}
      </div>
      {bottom}
    </div>
  );
};

const _HyamPrismicDocumentMenu = (props: HyamPrismicDocumentMenuProps) => {
  const styles = props.styles as HyamPrismicDocumentMenuStyles;
  const { document } = props;
  const { data } = document;

  const items0 = data?.items as HyamMenuItem[];
  const [items1, setItems1] = useState([] as HyamMenuItem[]);
  const [items2, setItems2] = useState([] as HyamMenuItem[]);
  const [anchor1, setAnchor1] = useState<number | null>(null);
  const [anchor2, setAnchor2] = useState<number | null>(null);

  const [activeItemCol0, setActiveItemCol0] = useState(-1 as any);

  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimer1 = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer2 = useRef<ReturnType<typeof setTimeout>>();

  /** Center of an item's row within the menu, used to anchor the column it opens. */
  const rowCenter = (el: HTMLElement) => {
    const container = containerRef.current;
    if (!container) return 0;
    const rect = el.getBoundingClientRect();
    return rect.top + rect.height / 2 - container.getBoundingClientRect().top;
  };

  const closeCol2 = () => {
    setItems2([]);
    setAnchor2(null);
  };

  const closeCol1 = () => {
    setItems1([]);
    setAnchor1(null);
    setActiveItemCol0(-1);
    closeCol2();
  };

  useEffect(
    () => () => {
      clearTimeout(closeTimer1.current);
      clearTimeout(closeTimer2.current);
    },
    [],
  );

  const doClose = () => {
    dispatchEvent({ name: "hide_nav_bar_drawer" });
    setTimeout(() => {
      closeCol1();
    }, 500);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.left_edge} />
      <Column
        {...props}
        doClose={doClose}
        col={0}
        items={items0}
        onColumnEnter={() => clearTimeout(closeTimer2.current)}
        onHover={(items, index, el) => {
          clearTimeout(closeTimer1.current);

          // No submenu: close col1 (and col2 with it), but only after the grace
          // period, so crossing this item on the way to col1 does not close it.
          if (!items?.length) {
            closeTimer1.current = setTimeout(closeCol1, CLOSE_DELAY);
            return;
          }

          clearTimeout(closeTimer2.current);
          setAnchor1(rowCenter(el));
          setItems1(items);

          // active item in col 0 changed, reset items in col2
          if (index !== activeItemCol0) closeCol2();
          setActiveItemCol0(index);
        }}
        bottom={
          <div className={styles.bottom_items_container}>
            <HyamPrismicButton
              rightIcon={<Arrow />}
              styles={{ container: styles.cta }}
              field={data?.cta_link}
            >
              {data?.cta_label}
            </HyamPrismicButton>
            <HyamPrismicLangSwitchRow />
          </div>
        }
      />
      <Column
        {...props}
        doClose={doClose}
        col={1}
        items={items1}
        anchorCenter={anchor1}
        onColumnEnter={() => {
          clearTimeout(closeTimer1.current);
          clearTimeout(closeTimer2.current);
        }}
        onHover={(items, index, el) => {
          clearTimeout(closeTimer2.current);

          if (!items?.length) {
            closeTimer2.current = setTimeout(closeCol2, CLOSE_DELAY);
            return;
          }

          setAnchor2(rowCenter(el));
          setItems2(items);
        }}
      />
      <Column
        {...props}
        doClose={doClose}
        col={2}
        items={items2}
        anchorCenter={anchor2}
        onColumnEnter={() => {
          clearTimeout(closeTimer1.current);
          clearTimeout(closeTimer2.current);
        }}
      />
      <div className={styles.right_edge} onClick={doClose} />
      <div className={styles.close_button}>
        <CloseIcon onClick={doClose} />
      </div>
    </div>
  );
};

export const HyamPrismicDocumentMenu = withConfigure<
  HyamPrismicDocumentMenuProps,
  HyamPrismicDocumentMenuStyles
>({
  componentName: "HyamPrismicDocumentMenu",
  Component: _HyamPrismicDocumentMenu,
  moduleStyles,
});
